import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { STAGE_MAP } from './crmData'
import { Kpi, SearchInput, SegTabs, crmToast, CrmToaster } from './crmUi'
import { Metric } from './Metric'
import { Button } from '../shared/Button'
import { useAuth } from '../../contexts/AuthContext'
import {
  getPipelineReport, setStageOverride, getProbAudit, refreshPipeline, getUsers, takePipelineSnapshot, getPipelineAccuracy,
  type PipelineReport, type ProbAuditRow, type StageProb,
} from './crmApi'

/**
 * Analytics — pipeline reporting (Fi's PRD 3), single-screen layout: pinned header (KPIs,
 * filters, calculated/adjusted toggle), then TABS in a fixed-height container so only the tab
 * content scrolls. The funnel and the probability table are ONE merged table — a bar, the
 * three probability numbers, sample and money per stage, all in a row.
 */

const gbp = (n: number) => `£${Math.round(n).toLocaleString()}`

// (SpeedToContactTile / AdoptionStrip / GateFunnelStrip moved to the Task
//  Dashboard as the condensed FunnelPulse strip — Lee, 25 Aug 2026.)

const pctOf = (p: number | null | undefined) => (p == null ? '—' : `${(p * 100).toFixed(p >= 0.1 ? 0 : 1)}%`)
const stageLabel = (k: string) => STAGE_MAP[k]?.label || k.replace(/_/g, ' ')

const SOURCES = ['WeQuote Trial', 'WeQuote Customer Sync', 'Calendly', 'Zoho Import', 'Import']

const StageOverrideEditor: React.FC<{ p: StageProb; onSaved: () => void; canEdit: boolean }> = ({ p, onSaved, canEdit }) => {
  const [editing, setEditing] = useState(false)
  const [val, setVal] = useState(p.override != null ? String(Math.round(p.override * 100)) : '')
  const [reason, setReason] = useState('')
  const [provisional, setProvisional] = useState(p.calculated == null || p.lowConfidence)

  const save = async (clear = false) => {
    const n = clear ? null : Number(val) / 100
    if (!clear && (!Number.isFinite(n!) || n! < 0 || n! > 1)) { crmToast('Enter 0–100', 'error'); return }
    if (!reason.trim() || reason.trim().length < 3) { crmToast('A reason is required — it feeds the change log', 'error'); return }
    try {
      await setStageOverride(p.stage, { override: n, reason: reason.trim(), provisional })
      crmToast(clear ? 'Override cleared — calculated value applies' : `Override saved: ${val}%`, 'success')
      setEditing(false)
      onSaved()
    } catch (e) {
      // Server explains monotonic-order rejections in plain words — show them verbatim.
      crmToast((e as Error)?.message || 'Could not save (admin only)', 'error')
    }
  }

  if (!editing) {
    return (
      <div className="flex items-center gap-1.5">
        {p.override != null ? (
          <span className="text-amber-600 font-semibold" title={`${p.overrideBy || ''}${p.overrideAt ? ' · ' + new Date(p.overrideAt).toLocaleDateString() : ''}${p.overrideReason ? ' · ' + p.overrideReason : ''}`}>
            {pctOf(p.override)}{p.provisional && <span className="text-[9px] font-bold uppercase ml-1 text-txt-tertiary">seed</span>}
          </span>
        ) : (
          <span className="text-txt-tertiary">—</span>
        )}
        {canEdit && (
          <button onClick={() => setEditing(true)} className="text-[11px] text-brand-600 hover:underline" title="Set or change this stage's override">
            edit
          </button>
        )}
      </div>
    )
  }
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      <input value={val} onChange={(e) => setVal(e.target.value)} placeholder="%" className="w-12 px-1.5 py-1 rounded border border-border bg-surface text-xs text-right" />
      <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason…" className="w-32 px-1.5 py-1 rounded border border-border bg-surface text-xs" />
      <label className="flex items-center gap-1 text-[10px] text-txt-secondary cursor-pointer" title="Provisional seed — judgement until enough history exists">
        <input type="checkbox" checked={provisional} onChange={(e) => setProvisional(e.target.checked)} /> seed
      </label>
      <button onClick={() => save(false)} className="text-[11px] font-semibold text-brand-600">Save</button>
      {p.override != null && <button onClick={() => save(true)} className="text-[11px] text-red-600">Clear</button>}
      <button onClick={() => setEditing(false)} className="text-[11px] text-txt-tertiary">Cancel</button>
    </div>
  )
}

const SampleBadge: React.FC<{ p: StageProb | null }> = ({ p }) => (
  <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full whitespace-nowrap ${p?.lowConfidence ? 'text-amber-700' : 'text-emerald-700'}`} style={{ background: p?.lowConfidence ? '#FEF3C7' : '#D1FAE5' }}>
    {p ? `${p.sampleSize}${p.lowConfidence ? ' · low' : ''}` : '0'}
  </span>
)

export const CrmAnalytics: React.FC = () => {
  const { currentAgent } = useAuth()
  const isAdmin = currentAgent?.role === 'admin'
  const navigate = useNavigate()
  const drillParams = () => [owner && `owner=${owner}`, source && `source=${encodeURIComponent(source)}`, days && `days=${days}`].filter(Boolean).join('&')
  const drill = (extra: string, metric: string) => navigate(`/crm/records?${[extra, drillParams(), `metric=${metric}`].filter(Boolean).join('&')}`)

  const [report, setReport] = useState<PipelineReport | null>(null)
  const [audit, setAudit] = useState<ProbAuditRow[]>([])
  const [users, setUsers] = useState<any[]>([])
  const [owner, setOwner] = useState('')
  const [source, setSource] = useState('')
  const [days, setDays] = useState('')
  const [view, setView] = useState<'adjusted' | 'calculated'>('adjusted')
  const [tab, setTab] = useState<'stages' | 'owners' | 'deals' | 'audit' | 'accuracy'>('stages')
  const [accuracy, setAccuracy] = useState<any | null>(null)
  const [dealQ, setDealQ] = useState('')
  const [busy, setBusy] = useState(false)

  const load = () => {
    getPipelineReport({ owner: owner || undefined, source: source || undefined, days: days ? Number(days) : undefined })
      .then(setReport)
      .catch(() => crmToast('Could not load the pipeline report', 'error'))
    getProbAudit().then(setAudit).catch(() => {})
    getPipelineAccuracy().then(setAccuracy).catch(() => {})
  }
  useEffect(load, [owner, source, days])
  useEffect(() => { getUsers().then(setUsers).catch(() => {}) }, [])
  const userName = (id: string | null) => (id ? users.find((u) => String(u.id) === String(id))?.name || id : 'Unassigned')

  const recalc = async () => {
    setBusy(true)
    try { await refreshPipeline(); crmToast('Recalculated from latest history', 'success'); load() }
    catch { crmToast('Recalculate failed (admin only)', 'error') }
    finally { setBusy(false) }
  }

  const t = report?.totals
  const weightedShown = view === 'adjusted' ? t?.weighted : t?.weightedCalc
  const maxCount = useMemo(() => Math.max(1, ...(report?.stages.map((s) => s.count) || [1])), [report])

  const dealRows = useMemo(() => {
    const needle = dealQ.trim().toLowerCase()
    const list = report?.deals || []
    return (needle ? list.filter((d) => `${d.name} ${d.stage} ${d.source || ''}`.toLowerCase().includes(needle)) : list).slice(0, 100)
  }, [report, dealQ])

  const exportUrl = `/api/crm/pipeline/export.csv?${[owner && `owner=${owner}`, source && `source=${encodeURIComponent(source)}`, days && `days=${days}`].filter(Boolean).join('&')}`

  return (
    <div className="h-full flex flex-col bg-surface-secondary">
      <CrmToaster />

      {/* ── Pinned header: title, actions, filters, KPIs, tabs — never scrolls ── */}
      <div className="px-6 pt-5 pb-3 bg-surface border-b border-border shrink-0 space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-xl font-bold text-txt-primary">Analytics · Pipeline &amp; forecast</h1>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <select value={owner} onChange={(e) => setOwner(e.target.value)} className="rounded-lg border border-border bg-surface px-2 py-1.5 text-xs">
              <option value="">All owners</option>
              {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
            <select value={source} onChange={(e) => setSource(e.target.value)} className="rounded-lg border border-border bg-surface px-2 py-1.5 text-xs">
              <option value="">All sources</option>
              {SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <select value={days} onChange={(e) => setDays(e.target.value)} className="rounded-lg border border-border bg-surface px-2 py-1.5 text-xs">
              <option value="">All time</option>
              <option value="90">Last 90 days</option>
              <option value="30">Last 30 days</option>
            </select>
            <div className="flex items-center rounded-lg border border-border overflow-hidden">
              {(['adjusted', 'calculated'] as const).map((v) => (
                <button key={v} onClick={() => setView(v)} className={`px-2.5 py-1.5 text-xs font-semibold ${view === v ? 'bg-brand-600 text-white' : 'bg-surface text-txt-secondary hover:bg-surface-hover'}`}>
                  {v === 'adjusted' ? 'Adjusted' : 'Calculated'}
                </button>
              ))}
            </div>
            <a href={exportUrl} className="px-2.5 py-1.5 rounded-lg text-xs font-semibold border border-border text-txt-primary hover:bg-surface-hover">⬇ CSV</a>
            {isAdmin && (
              <button
                onClick={async () => {
                  try { const r: any = await takePipelineSnapshot(); crmToast(r.skipped ? String(r.skipped) : `Snapshot stored — ${r.deals} deals, £${Math.round(r.headlineWeighted).toLocaleString()} weighted`, 'success') }
                  catch { crmToast('Snapshot failed', 'error') }
                }}
                title="Store an immutable copy of the pipeline right now (month-end runs automatically)"
                className="px-2.5 py-1.5 rounded-lg text-xs font-semibold border border-border text-txt-primary hover:bg-surface-hover"
              >
                Snapshot
              </button>
            )}
            {isAdmin && <Button size="sm" loading={busy} onClick={recalc}>Recalculate</Button>}
          </div>
        </div>

        {/* Speed-to-contact / gates / adoption moved to the Task Dashboard as the
            condensed FunnelPulse strip (Lee, 25 Aug 2026) — Analytics keeps the
            forecast + funnel table as its heroes. */}

        {/* Figure hierarchy: THE forecast number is the hero; gross/commit/best-case support it;
            calculated-only + override effect are its context line. */}
        <div className="flex gap-2 flex-wrap items-stretch">
          {/* PRD v1.0 §11 — the trustworthy pipeline: Σ value of QUALIFIED open deals only.
              Raw/unweighted until Fi supplies the qualified-stage probability. */}
          {(report as any)?.qualified && (() => {
            const qp = (report as any).qualified
            return (
              <div className="flex-1 min-w-[280px] rounded-xl border border-emerald-300 bg-emerald-50/60 px-4 py-2.5">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-emerald-700">Qualified pipeline · raw</p>
                <p className="text-[26px] leading-8 font-bold text-emerald-700">{gbp(qp.gross)}</p>
                <p className="text-[11px] text-txt-tertiary">
                  {qp.count} qualified lead{qp.count === 1 ? '' : 's'}
                  {(qp.byGrade.A || qp.byGrade.B || qp.byGrade.C) ? ` · A ${qp.byGrade.A} / B ${qp.byGrade.B} / C ${qp.byGrade.C}` : ''}
                  {' · '}gate: ANL {qp.funnel.autoNewLead} → Working {qp.funnel.working} → Qualified {qp.funnel.qualified} → Demo {qp.funnel.demoScheduled}
                </p>
                <p className="text-[10px] text-emerald-800/70 mt-0.5">unweighted — Working/Nurture/Disqualified excluded; probability weighting arrives with Fi&apos;s follow-up</p>
              </div>
            )
          })()}
          <div className="flex-1 min-w-[250px] rounded-xl border border-brand-300 bg-brand-50/50 px-4 py-2.5">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-brand-700">{view === 'adjusted' ? 'Weighted forecast' : 'Calculated-only forecast'}</p>
            <p className="text-[26px] leading-8 font-bold text-brand-700">{t ? gbp(weightedShown || 0) : '…'}</p>
            <p className="text-[11px] text-txt-tertiary">
              calculated only {t ? gbp(t.weightedCalc) : '…'} · override effect{' '}
              <span className={t && t.overrideDelta !== 0 ? 'text-amber-600 font-semibold' : ''}>
                {t ? `${t.overrideDelta >= 0 ? '+' : '−'}${gbp(Math.abs(t.overrideDelta))}` : '…'}
              </span>
            </p>
          </div>
          <div className="grid grid-cols-3 gap-2 flex-[1.6] min-w-[360px]">
            <Metric id="gross_pipeline" label="Gross pipeline (Band A)" value={t ? gbp(t.gross) : '…'} />
            <Metric id="forecast_commit" value={report ? gbp(report.forecast.commit) : '…'} />
            <Metric id="gross_pipeline" label="Best case" value={report ? gbp(report.forecast.bestCase) : '…'} />
          </div>
          {/* Nurture + Closed Won — flagged at the top, visibly EXCLUDED from the headline. */}
          {report?.bands && (
            <div className="w-full flex items-center gap-2 flex-wrap text-[12px]">
              <button onClick={() => drill('band=B1', 'nurture_trial')} title="See the nurture records" className="px-2.5 py-1.5 rounded-lg border border-amber-300 bg-amber-50 text-amber-800 text-left hover:bg-amber-100">
                <span className="font-bold uppercase text-[10px] mr-1.5">Nurture · excluded</span>
                Trial nurture {gbp(report.bands.b1.weighted)} est. ({report.bands.b1.count}) · Win-back recoverable {gbp(report.bands.b2.recoverableArr)} ({report.bands.b2.count})
                {report.bands.saveAlerts > 0 && <span className="ml-1.5 font-bold text-rose-700">{report.bands.saveAlerts} cancellation request{report.bands.saveAlerts > 1 ? 's' : ''} — live revenue at risk</span>}
              </button>
              <span className="px-2.5 py-1.5 rounded-lg border border-emerald-300 bg-emerald-50 text-emerald-800">
                <span className="font-bold uppercase text-[10px] mr-1.5">Closed won · actual</span>
                {report.bands.closedWon.count} live · {gbp(report.bands.closedWon.arr)} ARR
              </span>
              <span className="text-txt-tertiary">Closed Won (actual) + weighted open pipeline = period forecast — never one blended number</span>
            </div>
          )}
        </div>

        <SegTabs
          tabs={[
            { key: 'stages', label: `Funnel & probabilities (${report?.stages.length ?? 0})` },
            { key: 'owners', label: `By owner (${report?.owners.length ?? 0})` },
            { key: 'deals', label: `Deals (${report?.totals.count ?? 0})` },
            { key: 'audit', label: `Audit (${audit.length})` },
            { key: 'accuracy', label: `Accuracy (${accuracy?.snapshotCount ?? 0} snaps)` },
          ]}
          value={tab}
          onChange={(k) => setTab(k as any)}
        />
      </div>

      {/* ── Fixed-height tab container — only its CONTENT scrolls ── */}
      <div className="flex-1 min-h-0 px-6 py-4">
        <div className="h-full rounded-xl border border-border bg-surface overflow-y-auto px-4 pb-4">
          {tab === 'stages' && (
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10 [&_th]:bg-surface [&_th]:pt-4">
                <tr className="text-left text-[11px] uppercase tracking-wide text-txt-tertiary border-b border-border">
                  <th className="py-2 pr-3 font-medium w-[15%]">Stage</th>
                  <th className="py-2 pr-3 font-medium w-[22%]">Open deals</th>
                  <th className="py-2 pr-3 font-medium">Calculated</th>
                  <th className="py-2 pr-3 font-medium">Override</th>
                  <th className="py-2 pr-3 font-medium">Applied</th>
                  <th className="py-2 pr-3 font-medium">Next-stage</th>
                  <th className="py-2 pr-3 font-medium">Sample</th>
                  <th className="py-2 font-medium text-right">Gross · weighted</th>
                </tr>
              </thead>
              <tbody>
                {report?.stages.map((s, i) => (
                  <React.Fragment key={s.stage}>
                    {(i === 0 || report.stages[i - 1].band !== s.band) && (
                      <tr>
                        <td colSpan={8} className={`pt-3 pb-1 text-[10px] font-bold uppercase tracking-wider ${s.band === 'A' ? 'text-brand-700' : 'text-amber-700'}`}>
                          {s.band === 'A' ? 'Band A — sales pipeline (feeds the headline)' : s.band === 'B1' ? 'Band B1 — trial nurture (excluded · estimated value)' : 'Band B2 — win-back (excluded · recoverable ARR)'}
                        </td>
                      </tr>
                    )}
                  <tr className="border-b border-border last:border-0 align-middle">
                    <td className="py-2 pr-3 text-txt-primary whitespace-nowrap">
                      {stageLabel(s.stage)}
                      {s.trialActiveGroup && <span className="ml-1.5 text-[9px] font-bold uppercase px-1 py-0.5 rounded bg-brand-50 text-brand-700 border border-brand-200" title="Part of the Trial Active group — active trials from product data">Trial Active</span>}
                      {(s.endingSoon ?? 0) > 0 && <span className="ml-1 text-[9px] font-bold px-1 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200" title="Trials ending within 7 days">{s.endingSoon} ending ≤7d</span>}
                    </td>
                    <td className="py-2 pr-3">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-3.5 bg-surface-tertiary rounded overflow-hidden min-w-[60px]">
                          <div className="h-full rounded" style={{ width: `${Math.max(2, (s.count / maxCount) * 100)}%`, background: STAGE_MAP[s.stage]?.color || '#2563EB' }} />
                        </div>
                        <button
                          onClick={() => drill(`stage=${s.stage}`, 'weighted_forecast')}
                          title="See the records behind this number"
                          className="text-[12px] text-brand-600 font-semibold w-9 text-right hover:underline"
                        >
                          {s.count}
                        </button>
                      </div>
                    </td>
                    <td className="py-2 pr-3 text-txt-secondary">{s.probs && s.probs.calculated != null ? pctOf(s.probs.calculated) : <span className="text-txt-tertiary">no data</span>}</td>
                    <td className="py-2 pr-3">{s.probs && <StageOverrideEditor p={s.probs} onSaved={load} canEdit={isAdmin} />}</td>
                    <td className="py-2 pr-3 font-semibold text-txt-primary">{s.probs ? pctOf(s.probs.applied) : '—'}</td>
                    <td className="py-2 pr-3 text-txt-secondary">{s.probs?.nextRate != null ? pctOf(s.probs.nextRate) : '—'}</td>
                    <td className="py-2 pr-3"><SampleBadge p={s.probs} /></td>
                    <td className="py-2 text-right text-txt-secondary whitespace-nowrap">
                      {gbp(s.gross)} · <span className="font-semibold text-txt-primary">{gbp(view === 'adjusted' ? s.weighted : s.weightedCalc)}</span>
                      {s.estimatedShare > 0 && <span className="text-[10px] text-txt-tertiary ml-1" title={`${Math.round(s.estimatedShare * 100)}% of this stage's value is estimated`}>est.</span>}
                    </td>
                  </tr>
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}

          {tab === 'owners' && (
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10 [&_th]:bg-surface [&_th]:pt-4">
                <tr className="text-left text-[11px] uppercase tracking-wide text-txt-tertiary border-b border-border">
                  <th className="py-2 pr-3 font-medium">Owner</th>
                  <th className="py-2 pr-3 font-medium text-right">Deals</th>
                  <th className="py-2 pr-3 font-medium text-right">Gross</th>
                  <th className="py-2 pr-3 font-medium text-right">Weighted</th>
                  <th className="py-2 pr-3 font-medium text-right">Calculated only</th>
                  <th className="py-2 font-medium text-right">Commit (≥70%)</th>
                </tr>
              </thead>
              <tbody>
                {report?.owners.map((o) => (
                  <tr key={o.ownerId || 'un'} className="border-b border-border last:border-0">
                    <td className="py-2 pr-3 text-txt-primary">{userName(o.ownerId)}</td>
                    <td className="py-2 pr-3 text-right">
                      <button onClick={() => drill(o.ownerId ? `owner=${o.ownerId}&band=A` : 'band=A', 'weighted_forecast')} className="text-brand-600 font-semibold hover:underline" title="See this owner's records">{o.count}</button>
                    </td>
                    <td className="py-2 pr-3 text-right text-txt-secondary">{gbp(o.gross)}</td>
                    <td className="py-2 pr-3 text-right font-semibold text-txt-primary">{gbp(o.weighted)}</td>
                    <td className="py-2 pr-3 text-right text-txt-secondary">{gbp(o.weightedCalc)}</td>
                    <td className="py-2 text-right text-txt-secondary">{gbp(o.commit)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {tab === 'deals' && (
            <>
              <div className="mb-3 flex items-center gap-3">
                <SearchInput value={dealQ} onChange={setDealQ} placeholder="Search deals…" className="w-64" />
                <p className="text-[11px] text-txt-tertiary">Top 100 by weighted value · "est." = estimated (live MRR × 12 / plan average); a typed amount always wins</p>
              </div>
              <table className="w-full text-sm">
                <thead className="sticky top-0 z-10 [&_th]:bg-surface [&_th]:pt-4">
                  <tr className="text-left text-[11px] uppercase tracking-wide text-txt-tertiary border-b border-border">
                    <th className="py-2 pr-3 font-medium">Deal</th>
                    <th className="py-2 pr-3 font-medium">Stage</th>
                    <th className="py-2 pr-3 font-medium text-right">Value</th>
                    <th className="py-2 pr-3 font-medium text-right">Applied</th>
                    <th className="py-2 font-medium text-right">Weighted</th>
                  </tr>
                </thead>
                <tbody>
                  {dealRows.map((d) => (
                    <tr key={d.id} className="border-b border-border last:border-0">
                      <td className="py-1.5 pr-3 text-txt-primary truncate max-w-[260px]">{d.name}</td>
                      <td className="py-1.5 pr-3 text-txt-secondary whitespace-nowrap">{stageLabel(d.stage)}</td>
                      <td className="py-1.5 pr-3 text-right text-txt-secondary whitespace-nowrap">{gbp(d.value)}{d.valueEstimated && <span className="text-[10px] text-txt-tertiary ml-1">est.</span>}</td>
                      <td className="py-1.5 pr-3 text-right">{pctOf(d.applied)}{d.overridden && <span className="text-[9px] font-bold uppercase text-amber-600 ml-1" title="Per-deal override active">ovr</span>}</td>
                      <td className="py-1.5 text-right font-semibold text-txt-primary">{gbp(view === 'adjusted' ? d.weighted : d.weightedCalc)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {tab === 'accuracy' && (
            accuracy == null ? <p className="text-sm text-txt-tertiary py-6 text-center">Loading…</p> : (
            <div className="space-y-5">
              <p className="text-[12px] text-txt-tertiary">
                Accuracy data starts at the baseline snapshot{accuracy.baseline ? ` (${new Date(accuracy.baseline).toLocaleDateString()})` : ''} — nothing before it exists, by design (snapshots are stored, never reconstructed).
              </p>

              <div>
                <p className="text-[13px] font-semibold text-txt-primary mb-1.5">Forecast vs actual</p>
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-[11px] uppercase tracking-wide text-txt-tertiary border-b border-border">
                    <th className="py-1.5 pr-3 font-medium">Snapshot</th><th className="py-1.5 pr-3 font-medium text-right">Forecast</th>
                    <th className="py-1.5 pr-3 font-medium text-right">Actual T+1</th><th className="py-1.5 pr-3 font-medium text-right">T+2</th>
                    <th className="py-1.5 pr-3 font-medium text-right">T+3</th><th className="py-1.5 font-medium text-right">Variance (T+1)</th>
                  </tr></thead>
                  <tbody>
                    {accuracy.forecastVsActual.map((s2: any) => (
                      <tr key={s2.id} className="border-b border-border last:border-0">
                        <td className="py-1.5 pr-3 text-txt-primary whitespace-nowrap">{new Date(s2.takenAt).toLocaleDateString()} <span className="text-[10px] text-txt-tertiary">{s2.trigger}</span></td>
                        <td className="py-1.5 pr-3 text-right font-semibold">{gbp(s2.forecast)}</td>
                        {s2.horizons.map((h2: any) => (
                          <td key={h2.months} className="py-1.5 pr-3 text-right text-txt-secondary">{h2.pending ? <span className="text-txt-tertiary">pending</span> : gbp(h2.actual)}</td>
                        ))}
                        <td className="py-1.5 text-right">{s2.horizons[0].pending ? <span className="text-txt-tertiary">—</span> : <span className={s2.horizons[0].variance >= 0 ? 'text-emerald-600' : 'text-red-600'}>{s2.horizons[0].variance >= 0 ? '+' : '−'}{gbp(Math.abs(s2.horizons[0].variance))}{s2.horizons[0].variancePct != null ? ` (${s2.horizons[0].variancePct}%)` : ''}</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div>
                <p className="text-[13px] font-semibold text-txt-primary mb-0.5">Observed stage conversion vs configured</p>
                <p className="text-[11px] text-txt-tertiary mb-1.5">Recommendations are suggested, never auto-applied — accept one via the stage's Override editor, with a reason. Suppressed below n={accuracy.minSample} closed outcomes.</p>
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-[11px] uppercase tracking-wide text-txt-tertiary border-b border-border">
                    <th className="py-1.5 pr-3 font-medium">Stage</th><th className="py-1.5 pr-3 font-medium text-right">Configured</th>
                    <th className="py-1.5 pr-3 font-medium text-right">Observed (T+3)</th><th className="py-1.5 pr-3 font-medium text-right">n</th>
                    <th className="py-1.5 font-medium">Recommendation</th>
                  </tr></thead>
                  <tbody>
                    {accuracy.stageConversion.map((sc: any) => (
                      <tr key={sc.stage} className="border-b border-border last:border-0">
                        <td className="py-1.5 pr-3 text-txt-primary whitespace-nowrap">{stageLabel(sc.stage)}</td>
                        <td className="py-1.5 pr-3 text-right text-txt-secondary">{pctOf(sc.configured)}</td>
                        <td className="py-1.5 pr-3 text-right">{sc.h3.observed != null ? pctOf(sc.h3.observed) : <span className="text-txt-tertiary">—</span>}</td>
                        <td className="py-1.5 pr-3 text-right text-txt-secondary">{sc.h3.resolved}</td>
                        <td className="py-1.5">{sc.insufficient ? <span className="text-[11px] text-amber-700">insufficient data — n={sc.h3.resolved}</span> : sc.recommendation != null ? <span className="text-[11px] font-semibold text-brand-600">suggest {Math.round(sc.recommendation * 100)}% — apply via Override</span> : <span className="text-[11px] text-emerald-700">calibrated</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div>
                <p className="text-[13px] font-semibold text-txt-primary mb-1.5">Slippage — open deals past their expected close (trial end)</p>
                <p className="text-[12px] text-txt-secondary mb-2">{accuracy.slippage.total.count} deals · {gbp(accuracy.slippage.total.value)} · avg {accuracy.slippage.total.avgDaysSlipped}d slipped</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <table className="w-full text-sm"><thead><tr className="text-left text-[11px] uppercase text-txt-tertiary border-b border-border"><th className="py-1 pr-3 font-medium">By stage</th><th className="py-1 pr-3 font-medium text-right">Count</th><th className="py-1 pr-3 font-medium text-right">Value</th><th className="py-1 font-medium text-right">Avg days</th></tr></thead>
                    <tbody>{accuracy.slippage.byStage.map((r2: any) => (<tr key={r2.stage} className="border-b border-border last:border-0"><td className="py-1 pr-3">{stageLabel(r2.stage)}</td><td className="py-1 pr-3 text-right">{r2.count}</td><td className="py-1 pr-3 text-right">{gbp(r2.value)}</td><td className="py-1 text-right">{r2.avgDaysSlipped}</td></tr>))}</tbody></table>
                  <table className="w-full text-sm"><thead><tr className="text-left text-[11px] uppercase text-txt-tertiary border-b border-border"><th className="py-1 pr-3 font-medium">By owner</th><th className="py-1 pr-3 font-medium text-right">Count</th><th className="py-1 pr-3 font-medium text-right">Value</th><th className="py-1 font-medium text-right">Avg days</th></tr></thead>
                    <tbody>{accuracy.slippage.byOwner.map((r2: any) => (<tr key={r2.ownerId} className="border-b border-border last:border-0"><td className="py-1 pr-3">{userName(r2.ownerId === 'unassigned' ? null : r2.ownerId)}</td><td className="py-1 pr-3 text-right">{r2.count}</td><td className="py-1 pr-3 text-right">{gbp(r2.value)}</td><td className="py-1 text-right">{r2.avgDaysSlipped}</td></tr>))}</tbody></table>
                </div>
              </div>

              <div>
                <p className="text-[13px] font-semibold text-txt-primary mb-1.5">Global calibration ratio — actual ÷ forecast</p>
                <p className="text-[12px] text-txt-secondary">
                  Trailing 6 months: {accuracy.calibration.m6.ratio != null ? <span className="font-semibold">{accuracy.calibration.m6.ratio}</span> : 'no elapsed snapshots yet'} ({accuracy.calibration.m6.snapshots} snapshots) ·
                  Trailing 12 months: {accuracy.calibration.m12.ratio != null ? <span className="font-semibold">{accuracy.calibration.m12.ratio}</span> : 'no elapsed snapshots yet'}
                </p>
                <p className="text-[11px] text-txt-tertiary mt-0.5">A stable ratio below 1 means the whole probability set is optimistic and can be scaled uniformly — works from the first elapsed month, no per-stage samples needed.</p>
              </div>
            </div>
            )
          )}

          {tab === 'audit' && (
            audit.length === 0 ? (
              <p className="text-sm text-txt-tertiary py-6 text-center">No overrides yet — the forecast is running purely on calculated values.</p>
            ) : (
              <div className="space-y-1.5">
                {audit.map((a) => (
                  <div key={a.id} className="flex items-start gap-2.5 text-[13px] border-b border-border last:border-0 pb-1.5">
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full shrink-0 ${a.scope === 'stage' ? 'text-purple-700' : 'text-teal-700'}`} style={{ background: a.scope === 'stage' ? '#EEEDFE' : '#E1F5EE' }}>
                      {a.scope}
                    </span>
                    <span className="flex-1 min-w-0 text-txt-primary truncate">
                      {a.refLabel || a.refId} · {a.oldValue != null ? pctOf(a.oldValue) : '—'} → <span className="font-semibold">{a.newValue != null ? pctOf(a.newValue) : 'cleared'}</span>
                      {a.reason && <span className="text-txt-secondary"> · "{a.reason}"</span>}
                    </span>
                    <span className="text-txt-tertiary text-[12px] whitespace-nowrap">{a.who} · {new Date(a.at).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                  </div>
                ))}
              </div>
            )
          )}
        </div>
      </div>
    </div>
  )
}
